// ImageInput component matching product-listing-crud-with-sidebar.html design
// Simple grid layout with upload area and main image selection

import React, { useState, useCallback } from 'react'
import { ImageItem, ImageInputProps } from './types'

// Utility function for class names
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ')
}

const ImageInput: React.FC<ImageInputProps> = ({
  images,
  onChange,
  maxImages = 10,
  disabled = false,
  error,
  className = '',
  id: providedId,
  'data-testid': dataTestId,
  ...props
}) => {
  const generatedId = `image-input-${Math.random().toString(36).substr(2, 9)}`
  const id = providedId || generatedId

  // Handle file uploads
  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !e.target.files) return
    
    const files = Array.from(e.target.files)
    const newImages: ImageItem[] = files.map((file) => ({
      id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      url: URL.createObjectURL(file),
      isMain: false, // User must manually select main image
      alt: file.name,
      file,
      name: file.name
    }))
    
    const updatedImages = [...images, ...newImages]
    onChange(updatedImages)
    
    // Reset input value to allow selecting same file again
    e.target.value = ''
  }, [images, onChange, disabled])

  // Set image as main
  const setAsMain = useCallback((index: number) => {
    if (disabled) return
    
    const updatedImages = images.map((img, i) => ({
      ...img,
      isMain: i === index
    }))
    onChange(updatedImages)
  }, [images, onChange, disabled])

  // Remove image
  const removeImage = useCallback((index: number) => {
    if (disabled) return
    
    const imageToDelete = images[index]
    if (imageToDelete.file && imageToDelete.url.startsWith('blob:')) {
      // Cleanup blob URL
      URL.revokeObjectURL(imageToDelete.url)
    }
    
    const updatedImages = images.filter((_, i) => i !== index)
    onChange(updatedImages)
  }, [images, onChange, disabled])

  // Check if any image is marked as main
  const hasMainImage = images.some(img => img.isMain)

  return (
    <div className={classNames('tw-flex tw-flex-col tw-gap-3 tw-pt-2', className)} data-testid={dataTestId} {...props}>
      {/* Header with image count */}
      <div className="tw-flex tw-justify-between tw-items-end">
        <label className="tw-text-sm tw-font-bold tw-text-gray-900 dark:tw-text-gray-100">
          Product Gallery Images
        </label>
        <span className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
          {images.length}/{maxImages} uploaded
        </span>
      </div>

      {/* Image Grid */}
      <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-5 tw-gap-4">
        {/* Upload Button - Always first */}
        <div className="tw-aspect-square tw-rounded-lg tw-border-2 tw-border-dashed tw-border-gray-300 dark:tw-border-gray-600 tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer hover:tw-border-blue-500 hover:tw-bg-blue-50 dark:hover:tw-bg-blue-900/20 tw-transition-all tw-group tw-relative tw-overflow-hidden">
          <span className="material-symbols-outlined tw-text-3xl tw-text-gray-400 group-hover:tw-text-blue-500 tw-transition-colors">
            add_photo_alternate
          </span>
          <span className="tw-text-xs tw-font-medium tw-text-gray-500 group-hover:tw-text-blue-500 tw-mt-2 tw-transition-colors">
            Add Image
          </span>
          <input 
            className="tw-absolute tw-inset-0 tw-opacity-0 tw-cursor-pointer" 
            type="file" 
            accept="image/*" 
            multiple 
            onChange={handleImageUpload}
            disabled={disabled || images.length >= maxImages}
          />
        </div>

        {/* Image Cards */}
        {images.map((image, index) => (
          <div key={image.id} className="tw-relative">
            {/* Image Container */}
            <div 
              className={classNames(
                'tw-aspect-square tw-rounded-lg tw-border tw-overflow-hidden tw-bg-gray-50 dark:tw-bg-gray-900',
                image.isMain 
                  ? 'tw-ring-2 tw-ring-blue-500 tw-ring-offset-2 tw-ring-offset-white dark:tw-ring-offset-gray-900 tw-border-gray-200 dark:tw-border-gray-700' 
                  : 'tw-border-gray-200 dark:tw-border-gray-700'
              )}
            >
              <img 
                src={image.url} 
                alt={image.alt} 
                className="tw-w-full tw-h-full tw-object-cover" 
                onError={(e) => {
                  // Handle broken image
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Im0xNSAxOSA0LTQgNCA0SDE1eiIgZmlsbD0iI0Q1RDdEQSIvPgo8Y2lyY2xlIGN4PSI5IiBjeT0iOSIgcj0iMiIgZmlsbD0iI0Q1RDdEQSIvPgo8L3N2Zz4K'
                }}
              />
              
              {/* Main image badge */}
              {image.isMain && (
                <div className="tw-absolute tw-top-2 tw-left-2 tw-bg-blue-500 tw-text-white tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium tw-flex tw-items-center tw-gap-1">
                  <span className="material-symbols-outlined tw-text-[14px]">star</span>
                  Main Image
                </div>
              )}

              {/* Delete button */}
              <button
                type="button"
                onClick={() => removeImage(index)}
                disabled={disabled}
                className="tw-absolute tw-top-2 tw-right-2 tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-rounded-full tw-p-1 tw-shadow-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                aria-label={`Remove ${image.alt}`}
              >
                <span className="material-symbols-outlined tw-text-[14px]">close</span>
              </button>
            </div>

            {/* Action Button */}
            <div className="tw-mt-2 tw-flex tw-gap-2">
              <button
                type="button"
                onClick={() => setAsMain(index)}
                disabled={disabled || image.isMain}
                className={classNames(
                  'tw-flex-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1',
                  image.isMain 
                    ? 'tw-bg-blue-500 tw-text-white' 
                    : 'tw-bg-gray-100 dark:tw-bg-gray-800 tw-text-gray-700 dark:tw-text-gray-300 hover:tw-bg-gray-200 dark:hover:tw-bg-gray-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed'
                )}
              >
                <span className="material-symbols-outlined tw-text-[14px]">star</span>
                <span>{image.isMain ? 'Main Image' : 'Set as Main'}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Upload instructions */}
      <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
        Upload up to {maxImages} images (max 5MB each). You must select a main image before proceeding.
      </p>

      {/* No main image warning */}
      {images.length > 0 && !hasMainImage && (
        <div className="tw-mt-2 tw-p-3 tw-bg-amber-50 dark:tw-bg-amber-900/20 tw-border tw-border-amber-200 dark:tw-border-amber-800 tw-rounded-lg">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-amber-700 dark:tw-text-amber-300">
            <span className="material-symbols-outlined tw-text-[16px]">warning</span>
            <span className="tw-text-xs tw-font-medium">
              Please select a main image by clicking "Set as Main" on one of your uploaded images.
            </span>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="tw-text-sm tw-text-red-600 tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-p-3 tw-rounded-lg" role="alert">
          {error}
        </div>
      )}
    </div>
  )
}

export default ImageInput